import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';

import { ProjectType } from '../enums/project.enum';

import { ApiService } from './api.service';
/**
 * Reference migration onto {@link ApiService}: the base URL and query-param
 * construction now live in one place instead of being rebuilt here.
 *
 * Methods that previously hand-concatenated query strings with `|| ''`
 * fallbacks keep doing so, inside the path passed to `api`. Those send empty
 * parameters (`?searchTerm=&factoryId=`) and switching them to `buildParams`
 * would drop those keys from the wire - a backend-visible change that needs
 * confirming against the API before it is made.
 */
@Injectable({
  providedIn: 'root'
})
export class ProjectService {
  private readonly endPoint = 'projects/';
  private readonly api = inject(ApiService);
  getAllV3WithFilters(options?: {
    searchTerm?: string;
    brandId?: string;
    factoryId?: string;
    consultantId?: string;
    page?: number;
    take?: number;
    projectType?: ProjectType | string;
  }): Observable<any> {
    // buildParams drops null/undefined/'', which matches the `if (...)` guards
    // this method used before, so the request is unchanged.
    return this.api.get(this.endPoint, {
      searchTerm: options?.searchTerm,
      brandId: options?.brandId,
      factoryId: options?.factoryId,
      consultantId: options?.consultantId,
      projectType: options?.projectType,
      page: options?.page ?? 1,
      take: options?.take ?? 100
    });
  }
  getV3ById(id: string): Observable<any> {
    return this.api.get<any>(`${this.endPoint}${id}`);
  }
  getProjectInfoById(id: string): Observable<any> {
    return this.api.get<any>(`${this.endPoint}${id}/infos`);
  }
  createV3(payload: any): Observable<any> {
    return this.api.post<any>(this.endPoint, payload);
  }
  updateV3(id: string, payload: any): Observable<any> {
    return this.api.patch<any>(`${this.endPoint}${id}`, payload);
  }
  updateAuditStatus(id: string, payload: any): Observable<any> {
    return this.api.patch<any>(`${this.endPoint}${id}/audit-status`, payload);
  }
  deleteV3(id: string): Observable<any> {
    return this.api.delete(`${this.endPoint}${id}`);
  }
  getProjectById(projectId: string, factoryId?: string): Observable<any> {
    return this.api.get(this.endPoint + projectId + `?factory=${factoryId || ''}`);
  }
  getProjectFilterData(projectId: string): Observable<any> {
    return this.api.get(this.endPoint + `${projectId}/factories/additional-infos`);
  }
  getProjectStateData(projectId: string, countryId: string): Observable<any> {
    return this.api.get(this.endPoint + `state/${projectId}?country=${countryId}`);
  }
  getProjectCityData(projectId: string, stateId: string): Observable<any> {
    return this.api.get(this.endPoint + `city/${projectId}?state=${stateId}`);
  }
  getProjectFactory(options: { projectId?: string; search_term?: string }): Observable<any> {
    return this.api.get(
      this.endPoint +
        `${
          options.projectId
        }/factory/?country=all&state=all&city=all&area=all&factory_types=all&unit_id=3&search_term=${
          options.search_term || ''
        }`
    );
  }
  getProjectModule(options: {
    projectId: string;
    production?: string;
    factory_id?: string;
  }): Observable<any> {
    return this.api.get(
      this.endPoint +
        `${options.projectId}/modules?production=${options.production || ''}&factory_id=${
          options.factory_id || ''
        }`
    );
  }
  getById(id: any): Observable<any> {
    return this.api.get(`${this.endPoint}${id}?limit=all`);
  }
  getAuditInfo(projectId: string): Observable<any> {
    return this.api.get(this.endPoint + `audit/${projectId}`);
  }
  createAudit(payload: any): Observable<any> {
    return this.api.post(this.endPoint + 'audits', payload);
  }
  getProjectModules(projectId: string, productionYesOrNo = 'no'): Observable<any> {
    return this.api.get(`${this.endPoint}${projectId}/modules?production=${productionYesOrNo}`);
  }
  getAllWithFilters(options?: {
    searchTerm?: string;
    factory?: string;
    brand?: string;
    groupOfFactory?: string;
    take?: any;
    page?: any;
    typeOfProject?: any;
  }): Observable<any> {
    return this.api.get(
      `${this.endPoint}?searchTerm=${options?.searchTerm || ''}&factoryId=${options?.factory || ''}&brand=${options?.brand || ''}&typeOfProject=${options?.typeOfProject || ''}&groupOfFactory=${options?.groupOfFactory || ''}&page=${options?.page || 1}&take=${options?.take || 100}`
    );
  }
  getFilterByBrandAndFactory(options: { factory: string; brand: string }): Observable<any> {
    return this.api.get(
      `${this.endPoint}filter-with-brand-and-factory?factory=${options?.factory || ''}&brand=${
        options?.brand || ''
      }`
    );
  }
  create(payload: any): Observable<any> {
    return this.api.post(this.endPoint, payload);
  }
  createMany(payload: any[]): Observable<any> {
    return this.api.post(this.endPoint + 'bulk-create', payload);
  }
  update(id: string, payload: any): Observable<any> {
    return this.api.patch(this.endPoint + id, payload);
  }
  delete(id: string): Observable<any> {
    return this.api.delete(this.endPoint + id);
  }
  getChemicalManagementSummary(
    project: string,
    reportFor: string,
    factory: string
  ): Observable<any> {
    return this.api.get(
      this.endPoint +
        'chemical-management/summary-new?project=' +
        project +
        '&reportFor=' +
        reportFor +
        '&factories[]=' +
        factory
    );
  }
  getChemicalManagementReport(assessment: string): Observable<any> {
    return this.api.get(
      this.endPoint + 'chemical-management/generate-report?assessment=' + assessment
    );
  }
  getWasteManagementSummary(project: string, reportFor: string): Observable<any> {
    return this.api.get(
      this.endPoint + 'waste/summary?project=' + project + '&reportFor=' + reportFor
    );
  }
  changeWasteStatus(projectId: string, payload: any): Observable<any> {
    return this.api.patch(this.endPoint + 'change-waste-status/' + projectId, payload);
  }
  //Create Log for Project factory
  createProjectFactoryLog(payload: any): Observable<any> {
    return this.api.post(this.endPoint + 'create-project-factory-log', payload);
  }
}
